// Assumes Flickity is globally available (or imported above)

export function flickityPagination() {
  document.querySelectorAll('.js-flickity').forEach((carouselElem) => {
    // Prevent double-init
    if (carouselElem.__flktyInit) return;
    carouselElem.__flktyInit = true;

    // Parse options from data attribute
    const dataOpts = carouselElem.getAttribute('data-flickity');
    let options = dataOpts ? JSON.parse(dataOpts) : {};

    // Respect reduced motion
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      options = {
        ...options,
        autoPlay: false,
        draggable: false,
        wrapAround: false,
      };
    }

    const masthead = carouselElem.closest('.farlo-ui-slider-masthead');
    masthead?.classList.add('is-preparing');

    // --- Ready toggler (idempotent) ---
    let madeReady = false;
    const markReady = () => {
      if (madeReady) return;
      madeReady = true;
      masthead?.classList.remove('is-preparing');
      masthead?.classList.add('is-ready');
    };

    // Ensure we capture ready even if it fires during construction
    options.on = { ...(options.on || {}), ready: () => markReady() };

    // Wait for the first image to decode for a crisp first paint
    const firstImg = carouselElem.querySelector('.slider-cell__image img, .slider-cell__img');
    const waitForFirstImage = () => {
      if (!firstImg) return Promise.resolve();
      try {
        return firstImg.decode ? firstImg.decode() : Promise.resolve();
      } catch {
        return Promise.resolve();
      }
    };

    waitForFirstImage().then(() => {
      const flkty = new Flickity(carouselElem, options);

      // Safety fallbacks in case 'ready' was missed
      requestAnimationFrame(() => requestAnimationFrame(markReady));
      setTimeout(markReady, 2000);

      let prevIndex = flkty.selectedIndex;
      let isPaused = false;

      const playPauseBtn = masthead?.querySelector('.js-slide-playpause');
      const autoPlayVal = flkty.options.autoPlay;
      const duration =
        typeof autoPlayVal === 'number' ? autoPlayVal : (autoPlayVal ? 3000 : 3000);

      // --- Play/Pause button ---
      if (playPauseBtn && masthead) {
        playPauseBtn.addEventListener('click', () => {
          if (!isPaused) {
            flkty.pausePlayer();
            masthead.classList.add('is-paused');
            playPauseBtn.setAttribute('aria-label', 'Play Carousel');
          } else {
            flkty.unpausePlayer();
            masthead.classList.remove('is-paused');
            playPauseBtn.setAttribute('aria-label', 'Pause Carousel');
            // Restart current dot animation
            const currentInner = carouselElem.querySelector('.dot.is-animating .dot__progress-inner');
            if (currentInner) {
              currentInner.style.animation = 'none';
              void currentInner.offsetWidth; // reflow
              currentInner.style.animation = '';
            }
          }
          isPaused = !isPaused;
        });
      }

      // --- Dots with progress bars ---
      const initDots = () => {
        const dots = Array.from(
          carouselElem.querySelectorAll('.flickity-page-dots .dot')
        );
        if (!dots.length) return false;

        dots.forEach((dot) => {
          let wrap = dot.querySelector('.dot__progress');
          let inner = dot.querySelector('.dot__progress-inner');
          if (!wrap) {
            wrap = document.createElement('span');
            wrap.className = 'dot__progress';
            dot.appendChild(wrap);
          }
          wrap.style.setProperty('--progress-duration', `${duration}ms`);
          if (!inner) {
            inner = document.createElement('span');
            inner.className = 'dot__progress-inner';
            wrap.appendChild(inner);
          }
        });

        const clearAll = () => {
          dots.forEach((dot) => dot.classList.remove('is-completed', 'is-animating'));
        };

        flkty.on('select', () => {
          const idx = flkty.selectedIndex;

          const wrappedForward =
            (options.wrapAround || flkty.options.wrapAround) &&
            prevIndex === dots.length - 1 &&
            idx === 0;

          if (wrappedForward) {
            clearAll();
          } else if (prevIndex !== idx) {
            dots[prevIndex]?.classList.remove('is-animating');
            dots[prevIndex]?.classList.add('is-completed');
          }

          dots[idx]?.classList.remove('is-completed');
          dots[idx]?.classList.add('is-animating');

          prevIndex = idx;
        });

        dots[prevIndex]?.classList.add('is-animating');
        return true;
      };

      // Try a few frames until Flickity injects the dots
      let attempts = 0;
      const MAX_ATTEMPTS = 20;
      const tick = () => {
        if (initDots()) return;
        if (attempts++ < MAX_ATTEMPTS) requestAnimationFrame(tick);
      };
      tick();

      // Pause when tab hidden; resume (and restart progress anim) on return
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          flkty.pausePlayer();
        } else if (!isPaused && !reduceMotion) {
          flkty.unpausePlayer();
          const currentInner = carouselElem.querySelector('.dot.is-animating .dot__progress-inner');
          if (currentInner) {
            currentInner.style.animation = 'none';
            void currentInner.offsetWidth;
            currentInner.style.animation = '';
          }
        }
      });
    });
  });
}
